import { ActionButton, Divider, Icon, Link, ShortcutButton, Text } from '@stone-payments/jade'
import jadeTheme from '@stone-payments/jade-design-tokens/cjs/stone'
import { stringFormat } from 'shared/format-utils'
import styled from 'styled-components'

import { type Target, eventHelper } from '~/domains/home-v2/shared/helpers/event-helper'
import { useGetHome } from '~/domains/home-v2/shared/hooks/get-bff'
import { type Actions, type Analytics } from '~/domains/home-v2/types'
import { Flex } from '~/domains/platform/design-system'
import { Skeleton } from '~/domains/platform/design-system/skeleton/skeleton'
import { type BoxProps } from '~/domains/platform/design-system/types'
import { type Entity, type Subject } from '~/domains/platform/infra/deus-ex-machina/types'
import BlackBird, { type Routes } from '~/domains/platform/lib/blackbird'
import { useHiddenSensitive } from '~/domains/platform/lib/hidden-sensitive/hooks/use-hidden-sensitive'
import { Choose, For, If } from '~/domains/platform/lib/utilities-components'

import analitica from '../analitica'

const CustomFlex = styled(Flex)<{ disableHover?: boolean }>`
  padding: ${jadeTheme.global.space[150]};
  gap: ${jadeTheme.global.space[50]};
  border-radius: ${jadeTheme.global.space[100]};
  cursor: pointer;
  &:hover {
    background: ${props => (props.disableHover ? 'unset' : jadeTheme.theme.color.background['surface-hover'])};
    transition: 0.2s ease-out;
  }
`

const RefreshButton = styled(Flex)`
  align-self: flex-start;
  align-items: center;
  gap: ${jadeTheme.global.space[50]};
  border-radius: ${jadeTheme.global.space[50]};
  padding: ${jadeTheme.global.space[25]} ${jadeTheme.global.space[50]};
  /* compensa o padding lateral para o ícone continuar alinhado com o texto de cima */
  margin-left: -${jadeTheme.global.space[50]};
  &:hover {
    background: ${jadeTheme.theme.color.background['surface-hover']};
    transition: 0.2s ease-out;
  }
`

function BalanceCardSkeleton() {
  return (
    <>
      <CustomFlex flexDirection="column">
        <Skeleton width="40%" height={jadeTheme.global.space[250]} />
        <Skeleton width="80%" height={jadeTheme.global.space[250]} />
        <Skeleton width="80%" height={jadeTheme.global.space[250]} />
        <Skeleton width="80%" height={jadeTheme.global.space[250]} />
      </CustomFlex>
      <CustomFlex flexDirection="column">
        <Skeleton width="40%" height={jadeTheme.global.space[250]} />
        <Skeleton width="80%" height={jadeTheme.global.space[250]} />
      </CustomFlex>
    </>
  )
}

interface ErrorStateProps extends BoxProps {
  label: string
  onRefetch: () => void
}

function ErrorState({ label, onRefetch, ...flexProps }: ErrorStateProps) {
  return (
    <Flex flexDirection="column" gap={jadeTheme.global.space[50]} {...flexProps}>
      <Flex pr={jadeTheme.global.space[500]}>
        <Text variant="text-medium" weight="regular" color="medium" aria-hidden="true">
          Não foi possível carregar {label}
        </Text>
      </Flex>

      <RefreshButton
        role="button"
        aria-label="Atualizar"
        onClick={event => {
          event.stopPropagation()
          onRefetch()
        }}
      >
        <Icon size="small" use="action-refresh" color="brand" />
        <Text variant="text-medium" weight="medium" color="brand">
          Atualizar
        </Text>
      </RefreshButton>
    </Flex>
  )
}

export function BalanceCard({
  entity,
  subject,
  withTopDivider = true
}: {
  entity: Entity<'acquirer' | 'banking'>
  subject: Subject<'stone_account_resources'>
  withTopDivider?: boolean
}) {
  const { balance, isLoading, refetch } = useGetHome({
    document: subject?.document,
    accountId: entity?.paymentAccount?.id,
    globalId: entity.id,
    affiliationKey: entity?.stoneCodes[0]?.affiliationKey,
    stoneAccount: entity?.stoneAccount
  })

  const handleClick = ({ uri, analytics, label }: { uri: Routes; analytics: Array<Analytics>; label: Target }) => {
    const event = eventHelper[label]

    if (analytics?.length > 0 && event === null) {
      analytics
        .filter(event => event.type === 'CLICKED')
        .forEach(event => {
          analitica.events.balanceCard.actionClick({
            eventName: event?.eventName,
            type: event?.type,
            properties: event?.properties
          })
        })
    } else {
      analitica.events.balanceCard.actionClick({
        eventName: event.label,
        type: event.type,
        properties: event.properties
      })
    }

    BlackBird.travelTo(uri)
  }

  const [hiddenSensitive, triggerToggle] = useHiddenSensitive()

  const balanceError = Boolean(balance?.balance?.error)
  const scheduledError = Boolean(balance?.scheduled?.error)
  const blockedError = Boolean(balance?.blocked?.error)
  const hasBalanceError = balanceError || scheduledError || blockedError

  return (
    <If condition={balance?.show}>
      {withTopDivider ? <Divider direction="horizontal" size="small" /> : null}
      <Flex flexDirection="column" p={jadeTheme.global.space[100]} data-testid={balance?.id}>
        <Choose>
          <Choose.When condition={isLoading}>
            <BalanceCardSkeleton />
          </Choose.When>
          <Choose.Otherwise>
            <Flex position="relative">
              <Flex
                position="absolute"
                justifyContent="end"
                right={0}
                top={0}
                mr={jadeTheme.global.space[50]}
                mt={jadeTheme.global.space[50]}
                zIndex={1}
              >
                <ActionButton
                  aria-label={hiddenSensitive ? 'esconde valores' : 'mostra valores'}
                  onClick={() => {
                    triggerToggle()
                    analitica.events.balanceCard.hideBalance(hiddenSensitive ? 'hide' : 'show')
                  }}
                  active={hiddenSensitive}
                  icon={!hiddenSensitive ? 'eye-open' : 'eye-close'}
                />
              </Flex>
            </Flex>
            <CustomFlex
              onClick={() => {
                if (balance?.balance?.navigation?.uri) {
                  handleClick({
                    label: balance.balance.label as Target,
                    uri: balance.balance.navigation.uri,
                    analytics: balance?.balance?.analytics as Array<Analytics>
                  })
                }
              }}
              flexDirection="column"
              aria-label={
                balanceError
                  ? 'Não foi possível carregar Saldo'
                  : `${balance?.balance?.label}. Valor: ${
                      hiddenSensitive ? '••••' : stringFormat.currency(balance?.balance?.amount)
                    }. Clique para ver mais detalhes`
              }
              role="button"
              disableHover={hasBalanceError}
            >
              {balanceError && (
                <ErrorState label={balance?.balance?.label} onRefetch={refetch} mb={jadeTheme.global.space[25]} />
              )}

              {!balanceError && (
                <>
                  <Text variant="text-small" weight="regular" color="medium" aria-hidden="true">
                    {balance?.balance?.label}
                  </Text>
                  <Text variant="heading-medium" aria-hidden="true">
                    {hiddenSensitive ? '••••' : stringFormat.currency(balance?.balance?.amount)}
                  </Text>
                </>
              )}
              {balance?.scheduled?.show && !scheduledError && (
                <Flex gap={jadeTheme.global.space[50]}>
                  <Icon size="small" use="circle-clock" color="medium" />
                  <Text variant="text-small" weight="regular" color="medium">
                    {hiddenSensitive ? '••••' : stringFormat.currency(balance?.scheduled?.amount)}{' '}
                    {balance?.scheduled?.label}
                  </Text>
                </Flex>
              )}

              {balance?.blocked?.show && !blockedError && (
                <Flex gap={jadeTheme.global.space[50]}>
                  <Icon size="small" use="lock-close" color="medium" />
                  <Text variant="text-small" weight="regular" color="medium">
                    {hiddenSensitive ? '••••' : stringFormat.currency(balance?.blocked?.amount).replace('-', '')}{' '}
                    {balance?.blocked?.label}
                  </Text>
                </Flex>
              )}
            </CustomFlex>

            <If condition={balance?.investments?.show}>
              <CustomFlex
                aria-label={`${balance?.investments?.title}. ${balance?.investments?.label}. Clique para ${(balance
                  ?.investments?.navigation?.text
                  ? balance?.investments?.navigation?.text
                  : ('Guardar' as string)
                )?.toLowerCase()}
                `}
                onClick={() => {
                  if (balance?.investments?.navigation?.uri) {
                    handleClick({
                      label: balance.investments.label as Target,
                      uri: balance.investments.navigation.uri,
                      analytics: balance?.investments?.analytics as Array<Analytics>
                    })
                  }
                }}
                flexDirection="column"
                role="button"
              >
                <Choose>
                  <Choose.When condition={balance?.investments?.amount > 0}>
                    <Text variant="text-small" weight="regular" color="medium">
                      {balance?.investments?.title ? balance?.investments?.title : balance?.investments?.label}
                    </Text>
                    <Text variant="heading-xsmall">
                      {hiddenSensitive ? '••••' : stringFormat.currency(balance?.investments?.amount)}
                    </Text>
                  </Choose.When>
                  <Choose.Otherwise>
                    <Text variant="text-medium" weight="medium" aria-hidden="true">
                      {balance?.investments?.title}
                    </Text>
                    <Text variant="text-small" weight="regular" color="medium" aria-hidden="true">
                      {balance?.investments?.label}
                    </Text>
                    <Link aria-hidden="true">
                      {balance?.investments?.navigation?.text ? balance?.investments?.navigation?.text : 'Guardar'}
                    </Link>
                  </Choose.Otherwise>
                </Choose>
              </CustomFlex>
            </If>
            <If condition={balance?.creditRevolving?.show}>
              <CustomFlex
                aria-label={`${balance?.creditRevolving?.title}. ${
                  balance?.creditRevolving?.description
                }. Clique para ${(balance?.creditRevolving?.navigation?.text as string)?.toLowerCase()}`}
                role="button"
                flexDirection="column"
                gap={jadeTheme.global.space[50]}
                onClick={() => {
                  if (balance?.creditRevolving?.navigation?.uri) {
                    handleClick({
                      label: balance.creditRevolving.title as Target,
                      uri: balance.creditRevolving.navigation.uri,
                      analytics: balance?.creditRevolving?.analytics as Array<Analytics>
                    })
                    return
                  }
                  BlackBird.reload()
                }}
              >
                <Text variant="text-medium" weight="medium" aria-hidden="true">
                  {balance?.creditRevolving?.title}
                </Text>
                <Text variant="text-small" weight="regular" color="medium" aria-hidden="true">
                  {balance?.creditRevolving?.description}
                </Text>
                <Link aria-hidden="true">{balance?.creditRevolving?.navigation?.text as string}</Link>
              </CustomFlex>
            </If>
            <Flex justifyContent="space-between" p={jadeTheme.global.space[100]}>
              <For
                of={balance?.actions as Array<Actions>}
                render={item => {
                  const { label, locked, icon, uri, analytics } = item

                  return (
                    <Flex key={label} position="relative">
                      <If condition={locked}>
                        <Flex
                          p={jadeTheme.global.space[50]}
                          borderRadius="1rem"
                          position="absolute"
                          right={0}
                          top={0}
                          bg={{ background: jadeTheme.theme.color.background.info }}
                        >
                          <Icon color="on-color-high" size="small" use="lock-close" />
                        </Flex>
                      </If>
                      <ShortcutButton
                        icon={icon}
                        label={label}
                        disabled={locked}
                        onClick={() => {
                          handleClick({
                            label: label as Target,
                            uri: uri,
                            analytics: analytics
                          })
                        }}
                      />
                    </Flex>
                  )
                }}
              />
            </Flex>
          </Choose.Otherwise>
        </Choose>
      </Flex>
    </If>
  )
}
